import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { setColumnWidth } from 'services/columnWidths/actions';
import { CROPSURE_PASSWORDS_LOAD_NAMESPACE } from 'services/cropsurePasswords/constants';

import {
    requestCreateCropsurePassword,
    requestDeleteCropsurePassword,
    requestUpdateCropsurePassword
} from 'services/cropsurePasswords/actions';

import VirtualizedTable from 'components/VirtualizedTable';

import {
    selectCropsurePasswordsTableColumns,
    selectCropsurePasswordItems,
    selectIsWritable
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    isWritable?: ReturnType<typeof selectIsWritable>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    createCropsurePassword: typeof requestCreateCropsurePassword;
    updateCropsurePassword: typeof requestUpdateCropsurePassword;
    deleteCropsurePassword: typeof requestDeleteCropsurePassword;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class CropsurePasswordsVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleCreate = (item: VirtualizedTableItem): void => {
        const { createCropsurePassword } = this.props;

        createCropsurePassword(item);
    };

    handleDelete = (items: VirtualizedTableItem): void => {
        const { deleteCropsurePassword } = this.props;

        if (items[0] && items[0].id) {
            deleteCropsurePassword(items[0] as VirtualizedTableItem);
        }
    };

    handleItemUpdate = (item: VirtualizedTableItem): void => {
        const { updateCropsurePassword } = this.props;
        const { key, ...payload } = item;

        updateCropsurePassword(payload);
    };

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleHiddenFormFields = (item: VirtualizedTableItem): string[] => {
        return ['updatedAt'];
    };

    handleSetItemFormat = (
        item: VirtualizedTableItem
    ): VirtualizedTableItem => {
        const formattedItem = { ...item };

        if (formattedItem.password && formattedItem.password.length) {
            const { length } = formattedItem.password;

            formattedItem.password =
                '*'.repeat(length - 4) +
                formattedItem.password.substring(length - 4);
        }

        return formattedItem;
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, isWritable } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                label="Cropsure Password"
                labelPlural="Cropsure Passwords"
                loadNamespace={CROPSURE_PASSWORDS_LOAD_NAMESPACE}
                enableDeleting={isWritable}
                selectable={isWritable}
                enableCreating
                enableItemEditing
                onDelete={this.handleDelete}
                onCreate={this.handleCreate}
                onItemUpdate={this.handleItemUpdate}
                hiddenFormFields={this.handleHiddenFormFields}
                setItemFormat={this.handleSetItemFormat}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: selectCropsurePasswordItems,
    columns: selectCropsurePasswordsTableColumns,
    isWritable: selectIsWritable
});

export default connect(mapStateToProps, {
    setColumnWidth,
    createCropsurePassword: requestCreateCropsurePassword,
    updateCropsurePassword: requestUpdateCropsurePassword,
    deleteCropsurePassword: requestDeleteCropsurePassword
})(CropsurePasswordsVirtualizedTable);
